import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { useLang } from '@shapeable/ui';
import { classNames } from '@shapeable/utils';
import { KELP_CONDITION_LEGEND } from '../../data';

const cls = classNames('kelp-condition-legend');

export type KelpConditionLegendProps = Classable;

type SwatchProps = {
  _color: string;
};

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.UNIT(1.5)} ${theme.UNIT(3)};
    padding-top: ${theme.UNIT(4)};
    max-width: 100%;
    box-sizing: border-box;
  `,
});

const ItemStyles = breakpoints({
  base: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.UNIT(1.25)};
    min-width: 0;
  `,
});

const SwatchStyles = breakpoints({
  base: css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ _color }: SwatchProps) => _color};
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
  `,
});

const LabelStyles = breakpoints({
  base: css`
    font-size: ${theme.FONT_SIZE(11)};
    font-weight: 400;
    line-height: 1.3;
    color: ${theme.COLOR('mid')};
  `,
  tablet: css`
    font-size: ${theme.FONT_SIZE(12)};
  `,
});

const My = {
  Container: styled.div`${ContainerStyles}`,
  Item: styled.div`${ItemStyles}`,
  Swatch: styled.span<SwatchProps>`${SwatchStyles}`,
  Label: styled.span`${LabelStyles}`,
};

export const KelpConditionLegend: Shapeable.FC<KelpConditionLegendProps> = (props) => {
  const { className } = props;
  const t = useLang();

  return (
    <My.Container
      className={cls.name(className)}
      role="list"
      aria-label={t('Kelp forest condition score legend')}
    >
      {KELP_CONDITION_LEGEND.map((item) => (
        <My.Item key={item.label} role="listitem">
          <My.Swatch _color={item.color} aria-hidden />
          <My.Label>{t(item.label)}</My.Label>
        </My.Item>
      ))}
    </My.Container>
  );
};

KelpConditionLegend.cls = cls;
